import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
  Paper,
  IconButton,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DocumentIcon,
  Delete as DeleteIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { UploadedFile } from '../types';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface DocumentUploadStepProps {
  uploadedFile: UploadedFile | null;
  setUploadedFile: (file: UploadedFile | null) => void;
  setSuccessMessage: (message: string | null) => void;
  onNext: () => void;
}

const DocumentUploadStep: React.FC<DocumentUploadStepProps> = ({
  uploadedFile,
  setUploadedFile,
  setSuccessMessage,
  onNext
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [pdfLoading, setPdfLoading] = useState<boolean>(false);

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });

  const getFileType = (file: File): 'pdf' | 'image' | 'document' => {
    if (file.type === 'application/pdf') return 'pdf';
    if (file.type.startsWith('image/')) return 'image';
    return 'document';
  };

  const getFileIcon = (type: 'pdf' | 'image' | 'document') => {
    const iconSize = isMobile ? 20 : 28;
    switch (type) {
      case 'pdf':
        return <PdfIcon sx={{ fontSize: iconSize, color: '#f44336' }} />;
      case 'image':
        return <ImageIcon sx={{ fontSize: iconSize, color: '#2196f3' }} />;
      case 'document':
        return <DocumentIcon sx={{ fontSize: iconSize, color: '#4caf50' }} />;
    }
  };

  const processFile = async (file: File) => {
    try {
      setPdfLoading(true);
      const fileType = getFileType(file);
      const preview = URL.createObjectURL(file);
      const base64 = await fileToBase64(file);
      setUploadedFile({ file, preview, type: fileType, base64 });
      setSuccessMessage('File uploaded successfully!');
    } catch (error) {
      console.error('Failed to process file:', error);
      setSuccessMessage('Failed to process file');
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target.files?.length) {
      await processFile(e.target.files[0]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleBoxClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (fileInputRef.current && !uploadedFile) {
        fileInputRef.current.click();
      }
    },
    [uploadedFile]
  );

  const removeFile = () => {
    if (uploadedFile?.preview) {
      URL.revokeObjectURL(uploadedFile.preview);
    }
    setUploadedFile(null);
    setNumPages(0);
    setCurrentPage(1);
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfLoading(false);
  };

  const onDocumentLoadError = () => {
    setPdfLoading(false);
    setSuccessMessage('Failed to load PDF document');
  };

  // Thumbnails
  const renderThumbnailPanel = () => {
    if (!uploadedFile || uploadedFile.type !== 'pdf' || numPages === 0) {
      return null;
    }

    return (
      <Box
        sx={{
          width: isMobile ? 90 : 130,
          minWidth: isMobile ? 90 : 130,
          maxWidth: isMobile ? 90 : 130,
          height: '100%',
          borderRight: '1px solid #e0e0e0',
          bgcolor: '#fafafa',
          overflowY: 'auto',
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: '#666',
            textAlign: 'center',
            fontSize: isMobile ? '10px' : '12px',
            mb: 1
          }}
        >
          Pages ({numPages})
        </Typography>

        {Array.from({ length: numPages }, (_, index) => {
          const pageNum = index + 1;
          const isCurrentPage = currentPage === pageNum;

          return (
            <Box
              key={pageNum}
              onClick={() => setCurrentPage(pageNum)}
              sx={{
                cursor: 'pointer',
                border: isCurrentPage ? '2px solid #1976d2' : '1px solid #ddd',
                borderRadius: 1,
                bgcolor: isCurrentPage ? '#e3f2fd' : 'white',
                overflow: 'hidden',
                transition: '0.2s',
                '&:hover': {
                  borderColor: '#1976d2',
                  transform: 'scale(1.02)',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                },
                width: '100%',
                height: isMobile ? 100 : 140,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                flexShrink: 0
              }}
            >
              <Document file={uploadedFile.preview}>
                <Page
                  pageNumber={pageNum}
                  height={isMobile ? 90 : 130}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                />
              </Document>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 2,
                  left: 2,
                  bgcolor: isCurrentPage ? '#1976d2' : 'rgba(0,0,0,0.6)',
                  color: 'white',
                  px: 0.5,
                  py: 0.2,
                  borderRadius: 0.5,
                  fontSize: '10px'
                }}
              >
                {pageNum}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  // Main PDF/Image/Other Viewer
  const renderMainViewer = () => {
    if (!uploadedFile) return null;

    if (uploadedFile.type === 'pdf') {
      return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Controls */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1,
              borderBottom: '1px solid #e0e0e0',
              bgcolor: 'white'
            }}
          >
            {isMobile ? (
              <FormControl size="small" sx={{ minWidth: 70 }}>
                <Select
                  value={currentPage.toString()}
                  onChange={(e) => setCurrentPage(Number(e.target.value))}
                  sx={{ fontSize: '10px' }}
                >
                  {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => (
                    <MenuItem key={pageNum} value={pageNum} sx={{ fontSize: '10px' }}>
                      P{pageNum}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Typography variant="caption" sx={{ fontSize: '12px', color: '#666' }}>
                Page {currentPage} of {numPages}
              </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                onClick={() => setScale(Math.max(0.5, scale - 0.1))}
                disabled={scale <= 0.5}
                size="small"
              >
                <ZoomOutIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'center' }}>
                {Math.round(scale * 100)}%
              </Typography>
              <IconButton
                onClick={() => setScale(Math.min(2.0, scale + 0.1))}
                disabled={scale >= 2.0}
                size="small"
              >
                <ZoomInIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Viewer */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              bgcolor: '#f5f5f5'
            }}
          >
            {pdfLoading && (
              <Box sx={{ py: 4 }}>
                <Typography variant="caption">Loading PDF...</Typography>
              </Box>
            )}
            <Document
              file={uploadedFile.preview}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
            >
              <Page
                pageNumber={currentPage}
                scale={scale}
                width={isMobile ? window.innerWidth * 0.9 : undefined}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          </Box>
        </Box>
      );
    }

    if (uploadedFile.type === 'image') {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: '#f5f5f5'
          }}
        >
          <img
            src={uploadedFile.preview}
            alt="Uploaded document"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              borderRadius: '4px'
            }}
          />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 4
        }}
      >
        {getFileIcon(uploadedFile.type || 'document')}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Document Ready
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Preview not available for this file type
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          mb: 2,
          color: '#333',
          fontSize: isMobile ? '11px' : '13px',
          fontWeight: 600,
          display: 'block'
        }}
      >
        Step 1: Upload Document
      </Typography>

      {!uploadedFile ? (
        // Upload Area
        <Box>
          <Box
            onClick={handleBoxClick}
            sx={{
              border: `2px dashed #bdbdbd`,
              borderRadius: 1,
              height: isMobile ? 250 : 400,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#fafafa',
              cursor: 'pointer',
              '&:hover': { bgcolor: '#f0f0f0', borderColor: '#9e9e9e' }
            }}
          >
            <UploadIcon sx={{ fontSize: isMobile ? 48 : 64, color: '#9e9e9e', mb: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Upload your document
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              Drag & drop or click to browse <br /> PDF, DOC, DOCX, JPG, PNG, GIF, WEBP
            </Typography>
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadIcon />}
              onClick={(e) => e.stopPropagation()}
              sx={{ mt: 2, backgroundColor: '#217346', '&:hover': { backgroundColor: '#1a5d37' } }}
            >
              Browse Files
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
                hidden
                onChange={handleUpload}
              />
            </Button>
          </Box>

          {/* Skip Option */}
          <Box
            sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              Or skip this step and proceed without uploading a document
            </Typography>
            <Button
              variant="outlined"
              onClick={onNext}
              startIcon={<ArrowForwardIcon />}
              sx={{
                color: '#666',
                borderColor: '#bdbdbd',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  borderColor: '#9e9e9e'
                }
              }}
            >
              Skip & Continue
            </Button>
          </Box>
        </Box>
      ) : (
        // Preview with Thumbnails + Viewer
        <Paper
          elevation={2}
          sx={{ mt: 1, display: 'flex', flexDirection: 'column', height: '80vh' }}
        >
          {/* File Info Header */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: '1px solid #e0e0e0' }}
          >
            {getFileIcon(uploadedFile.type || 'document')}
            <Box sx={{ ml: 1.5, flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap>
                {uploadedFile.file?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {uploadedFile.file?.size ? (uploadedFile.file.size / 1024 / 1024).toFixed(2) : '0'}{' '}
                MB • {(uploadedFile.type || 'document').toUpperCase()}
                {uploadedFile.type === 'pdf' &&
                  numPages > 0 &&
                  ` • ${numPages} page${numPages !== 1 ? 's' : ''}`}
              </Typography>
            </Box>
            <IconButton onClick={removeFile} color="error">
              <DeleteIcon />
            </IconButton>
          </Box>

          {/* Content: Thumbnails + Viewer */}
          <Box sx={{ flex: 1, display: 'flex', minHeight: 0, minWidth: 0 }}>
            {!isMobile && renderThumbnailPanel()}
            {renderMainViewer()}
          </Box>

          {/* Continue Button */}
          <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
            <Button
              variant="contained"
              onClick={onNext}
              fullWidth
              sx={{ backgroundColor: '#217346', '&:hover': { backgroundColor: '#1a5d37' } }}
            >
              Continue to Field Placement
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default DocumentUploadStep;
